import React from 'react';
import styled from 'styled-components';
import { getAuth } from 'firebase/auth';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { toast } from 'react-toastify';
import { Check } from '@styled-icons/fa-solid/Check';
import { X } from '@styled-icons/fa-solid/X';
import { API_URL } from '../../../constants/string-constants';

const RequestsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 16px;
  flex: 1;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: var(--clr-bg-secondary, #2a2a2a);
  }

  &::-webkit-scrollbar-thumb {
    background: var(--clr-border, #444);
    border-radius: 2px;
  }
`;

const RequestItem = styled.div`
  background-color: var(--clr-bg-tertiary, #333);
  border: 1px solid var(--clr-border, #444);
  border-radius: 8px;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: var(--clr-bg-hover, #3a3a3a);
  }
`;

const UserInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const UserNickname = styled.div`
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 4px;
  color: ${(props) => props.color || 'var(--clr-text, #ffffff)'};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
`;

const ActionButton = styled.button`
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  font-size: 12px;
  font-weight: 500;
  min-width: 36px;
  height: 32px;

  &.accept {
    background-color: var(--clr-success, #28a745);
    color: white;
    &:hover {
      background-color: #218838;
    }
  }

  &.decline {
    background-color: var(--clr-danger, #dc3545);
    color: white;
    &:hover {
      background-color: #c82333;
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  padding: 32px 16px;
  text-align: center;
  color: var(--clr-text-secondary, #aaa);
`;

const EmptyTitle = styled.h4`
  margin: 0 0 8px 0;
  color: var(--clr-text, #ffffff);
`;

const EmptyText = styled.p`
  margin: 0;
  line-height: 1.4;
`;

function FriendRequestsTab({ 
  friendRequests, 
  processing, 
  onRequestAction,
  loading 
}) {
  const getUserColor = (user) => {
    return user.custom_color || user.assigned_color || '#999999';
  };

  const handleRequestAction = async (requestId, action) => {
    try {
      onRequestAction(requestId, 'processing', true);
      const auth = getAuth();

      if (!auth.currentUser) {
        throw new Error('Please sign in to respond to friend requests');
      }

      const token = await auth.currentUser.getIdToken();
      const response = await fetch(API_URL + `friends/requests/${requestId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + token,
        },
        body: JSON.stringify({ action }),
      });

      if (response.ok) {
        const analytics = getAnalytics();
        logEvent(analytics, `friend_request_${action}`);

        onRequestAction(requestId, action, false);

        if (action === 'accept') {
          toast.success('Friend request accepted!');
        } else {
          toast.success('Friend request declined');
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to ${action} friend request`);
      }
    } catch (error) {
      console.error(`Error ${action}ing friend request:`, error);
      toast.error(error.message || `Failed to ${action} friend request`);
      onRequestAction(requestId, 'processing', false);
    }
  };

  if (loading) {
    return (
      <EmptyState>
        <EmptyTitle>Loading friend requests...</EmptyTitle>
      </EmptyState>
    );
  }

  return (
    <RequestsList>
      {friendRequests.length === 0 ? (
        <EmptyState>
          <EmptyTitle>No pending friend requests</EmptyTitle>
          <EmptyText>
            Click on any message to send a friend request
          </EmptyText>
        </EmptyState>
      ) : (
        friendRequests.map((request) => (
          <RequestItem key={request.id}>
            <UserInfo>
              <UserNickname color={getUserColor(request.sender)}>
                {request.sender.nickname || 'Anonymous User'}
              </UserNickname>
            </UserInfo>
            <ActionButtons>
              <ActionButton
                className='accept'
                onClick={() => handleRequestAction(request.id, 'accept')}
                disabled={processing.has(request.id)}
                title='Accept friend request'
              >
                <Check size={14} />
              </ActionButton>
              <ActionButton
                className='decline'
                onClick={() => handleRequestAction(request.id, 'decline')}
                disabled={processing.has(request.id)}
                title='Decline friend request'
              >
                <X size={14} />
              </ActionButton>
            </ActionButtons>
          </RequestItem>
        ))
      )}
    </RequestsList>
  );
}

export default FriendRequestsTab;